import React, { useState, useEffect } from 'react';
import { userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './UserManagement.css';

const UserManagement = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await userService.getAllUsers();
            setUsers(data);
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (userId, userName) => {
        if (!window.confirm(`Approve user ${userName}?`)) return;
        try {
            setLoading(true);
            await userService.approveUser(userId);
            await loadUsers();
            alert(`✅ ${userName} approved successfully!`);
        } catch (err) {
            console.error('Error approving user:', err);
            alert('Approval failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async (userId, userName) => {
        if (!window.confirm(`Reject user ${userName}?`)) return;
        try {
            setLoading(true);
            await userService.rejectUser(userId);
            await loadUsers();
            alert(`❌ ${userName} rejected.`);
        } catch (err) {
            console.error('Error rejecting user:', err);
            alert('Rejection failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, currentRole, userName) => {
        const newRole = currentRole === 'ADMIN' ? 'USER' : 'ADMIN';
        const roleText = newRole === 'ADMIN' ? 'Admin' : 'User';
        if (!window.confirm(`Change ${userName}'s role to "${roleText}"?`)) return;
        try {
            setLoading(true);
            await userService.updateUserRole(userId, newRole);
            await loadUsers();
            alert(`🔄 ${userName} role updated to "${roleText}"!`);
        } catch (err) {
            console.error('Error changing role:', err);
            alert('Role change failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (userId, newStatus, userName) => {
        const statusTexts = { ACTIVE: 'Active', PENDING: 'Pending', REJECTED: 'Deactivated' };
        const statusText = statusTexts[newStatus] || newStatus;
        if (!window.confirm(`Change ${userName}'s status to "${statusText}"?`)) return;
        try {
            setLoading(true);
            await userService.updateUserStatus(userId, newStatus);
            await loadUsers();
            alert(`✅ ${userName} status updated to "${statusText}"!`);
        } catch (err) {
            console.error('Error changing status:', err);
            alert('Status change failed: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`⚠️ WARNING: Permanently delete ${userName} and ALL their data (fields, analyses, messages)? This cannot be undone.`)) return;
        try {
            setLoading(true);
            await userService.deleteUser(userId);
            await loadUsers();
            alert(`🗑️ ${userName} and all their data have been permanently removed.`);
        } catch (err) {
            console.error('Error deleting user:', err);
            alert('Delete failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            PENDING: { text: 'Pending', className: 'status-pending' },
            ACTIVE: { text: 'Active', className: 'status-active' },
            REJECTED: { text: 'Deactivated', className: 'status-rejected' }
        };
        const badge = badges[status] || { text: status, className: 'status-unknown' };
        return <span className={`status-badge ${badge.className}`}>{badge.text}</span>;
    };

    const getRoleBadge = (role) => {
        const badges = {
            ADMIN: { text: 'Admin', className: 'role-admin' },
            USER: { text: 'User', className: 'role-user' }
        };
        const badge = badges[role] || { text: role, className: 'role-unknown' };
        return <span className={`role-badge ${badge.className}`}>{badge.text}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading && users.length === 0) {
        return (
            <div className="user-management">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading users...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="user-management">
                <div className="error-state">
                    <p>❌ {error}</p>
                    <button onClick={loadUsers} className="btn-retry">Retry</button>
                </div>
            </div>
        );
    }

    const pendingUsers = users.filter(u => u.status === 'PENDING');
    const activeUsers = users.filter(u => u.status === 'ACTIVE');
    const rejectedUsers = users.filter(u => u.status === 'REJECTED');

    return (
        <div className="user-management">
            <div className="user-management-header">
                <div>
                    <h2>👥 User Management</h2>
                    <p className="subtitle">Manage registered users and assign roles</p>
                </div>
                <button onClick={loadUsers} className="btn-refresh" disabled={loading}>
                    🔄 Refresh
                </button>
            </div>

            <div className="stats-summary">
                <div className="stat-item">
                    <span className="stat-number">{users.length}</span>
                    <span className="stat-label">Total Users</span>
                </div>
                <div className="stat-item pending">
                    <span className="stat-number">{pendingUsers.length}</span>
                    <span className="stat-label">Pending</span>
                </div>
                <div className="stat-item active">
                    <span className="stat-number">{activeUsers.length}</span>
                    <span className="stat-label">Active</span>
                </div>
                <div className="stat-item rejected">
                    <span className="stat-number">{rejectedUsers.length}</span>
                    <span className="stat-label">Inactive</span>
                </div>
            </div>

            {users.length === 0 ? (
                <div className="empty-state">
                    <p>No registered users found.</p>
                </div>
            ) : (
                <div className="users-table-container">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Status</th>
                                <th>Role</th>
                                <th>Registered</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr
                                    key={user.id}
                                    className={`user-row status-${user.status.toLowerCase()}`}
                                    onClick={() => setSelectedUser(user)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="user-info-cell">
                                        <div className="user-avatar">
                                            {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                                        </div>
                                        <div className="user-details">
                                            <div className="user-name">{user.name || 'Unnamed'}</div>
                                            <div className="user-email">{user.email}</div>
                                        </div>
                                    </td>
                                    <td>{getStatusBadge(user.status)}</td>
                                    <td>{getRoleBadge(user.role)}</td>
                                    <td className="date-cell">{formatDate(user.createdAt)}</td>
                                    <td className="actions-cell">
                                        <div className="action-buttons">
                                            {user.firebaseUid === currentUser?.uid ? (
                                                <span className="self-label">(You)</span>
                                            ) : (
                                                <>
                                                    {user.status === 'PENDING' && (
                                                        <>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleApprove(user.id, user.name || user.email); }}
                                                                className="btn-action btn-approve"
                                                                disabled={loading}
                                                                title="Approve"
                                                            >
                                                                ✅ Approve
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleReject(user.id, user.name || user.email); }}
                                                                className="btn-action btn-reject"
                                                                disabled={loading}
                                                                title="Reject"
                                                            >
                                                                ❌ Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {user.status === 'ACTIVE' && (
                                                        <>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleRoleChange(user.id, user.role, user.name || user.email); }}
                                                                className="btn-action btn-role"
                                                                disabled={loading}
                                                                title={`Make ${user.role === 'ADMIN' ? 'User' : 'Admin'}`}
                                                            >
                                                                🔄 {user.role === 'ADMIN' ? 'Make User' : 'Make Admin'}
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleStatusToggle(user.id, 'REJECTED', user.name || user.email); }}
                                                                className="btn-action btn-deactivate"
                                                                disabled={loading}
                                                                title="Deactivate"
                                                            >
                                                                🚫 Deactivate
                                                            </button>
                                                        </>
                                                    )}
                                                    {user.status === 'REJECTED' && (
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleStatusToggle(user.id, 'ACTIVE', user.name || user.email); }}
                                                                className="btn-action btn-reactivate"
                                                                disabled={loading}
                                                                title="Activate"
                                                            >
                                                                🔓 Activate
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.id, user.name || user.email); }}
                                                                className="btn-action btn-delete-permanent icon-only"
                                                                disabled={loading}
                                                                title="Permanently Delete"
                                                                style={{ padding: '0.5rem' }}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedUser && (
                <div className="user-modal-overlay" onClick={() => setSelectedUser(null)}>
                    <div className="user-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="user-modal-header">
                            <h3>User Details</h3>
                            <button onClick={() => setSelectedUser(null)} className="modal-close-btn">×</button>
                        </div>
                        <div className="user-modal-body">
                            <div className="user-modal-avatar">
                                {selectedUser.firstName?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="user-modal-info-grid">
                                <div className="user-modal-info-item">
                                    <label>First Name</label>
                                    <span>{selectedUser.firstName || '-'}</span>
                                </div>
                                <div className="user-modal-info-item">
                                    <label>Last Name</label>
                                    <span>{selectedUser.lastName || '-'}</span>
                                </div>
                                <div className="user-modal-info-item full-width">
                                    <label>Email</label>
                                    <span>{selectedUser.email}</span>
                                </div>
                                <div className="user-modal-info-item">
                                    <label>University</label>
                                    <span>{selectedUser.university || '-'}</span>
                                </div>
                                <div className="user-modal-info-item">
                                    <label>Title</label>
                                    <span>{selectedUser.title || '-'}</span>
                                </div>
                                <div className="user-modal-info-item">
                                    <label>Department</label>
                                    <span>{selectedUser.department || '-'}</span>
                                </div>
                                <div className="user-modal-info-item">
                                    <label>Phone</label>
                                    <span>{selectedUser.phone || '-'}</span>
                                </div>
                                <div className="user-modal-info-item">
                                    <label>Status</label>
                                    {getStatusBadge(selectedUser.status)}
                                </div>
                                <div className="user-modal-info-item">
                                    <label>Role</label>
                                    {getRoleBadge(selectedUser.role)}
                                </div>
                                <div className="user-modal-info-item full-width">
                                    <label>Registered</label>
                                    <span>{formatDate(selectedUser.createdAt)}</span>
                                </div>
                            </div>

                            {selectedUser.firebaseUid !== currentUser?.uid && (
                                <div className="modal-danger-zone" style={{ marginTop: '2rem', padding: '1.5rem', background: '#fff5f5', borderRadius: '12px', border: '1px dashed #feb2b2' }}>
                                    <h4 style={{ margin: '0 0 0.5rem', color: '#c53030' }}>⚠️ Danger Zone</h4>
                                    <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#742a2a' }}>
                                        Permanently removes this user and all their data (fields, messages, analyses) from the system.
                                    </p>
                                    <button
                                        onClick={() => { handleDeleteUser(selectedUser.id, selectedUser.name || selectedUser.email); setSelectedUser(null); }}
                                        className="btn-delete-modal"
                                        style={{ width: '100%', padding: '0.75rem', background: '#f56565', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                        🗑️ Permanently Delete User
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
